import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { RosterConfig, SiteConfig, SiteFieldKey } from "../services/model";
import {
  ConfigureRosterRequest,
  ConfigureSiteRequest,
} from "../services/request";
import {
  useConfigureRosterMutation,
  useConfigureSiteMutation,
  useMoveRosterMutation,
  useSiteConfigurationApiService,
  useSitesAndRegionsTreeApiService,
} from "../services/api/SiteConfigurationApiService";

export interface PostSaveAlertState {
  field: string;
  oldValue: string;
  newValue: string;
  updated: string[];
  skipped: string[];
}

export interface MovedAlertState {
  rosterName: string;
  destination: string;
}

const cascadeFieldLabel: Record<SiteFieldKey, string> = {
  siteName: "Site name",
  address: "Address",
  latitude: "Latitude",
  longitude: "Longitude",
  geofenceRadius: "Geofence radius",
  lateArrivalThreshold: "Late arrival threshold",
};

const EMPTY_SITE: SiteConfig = {
  id: "",
  siteName: "",
  address: "",
  latitude: "",
  longitude: "",
  geofenceRadius: "",
  lateArrivalThreshold: "",
};

export interface UseSiteConfigurationResult {
  notFound: boolean;
  isLoading: boolean;
  isError: boolean;
  site: SiteConfig;
  savedSite: SiteConfig;
  rosters: RosterConfig[];
  lastSavedRosters: Record<string, RosterConfig>;
  siteDirty: boolean;
  siteDirtyField: SiteFieldKey | null;
  postSaveAlert: PostSaveAlertState | null;
  setPostSaveAlert: Dispatch<SetStateAction<PostSaveAlertState | null>>;
  auditDrawerOpen: boolean;
  setAuditDrawerOpen: Dispatch<SetStateAction<boolean>>;
  pendingRevertId: string | null;
  setPendingRevertId: Dispatch<SetStateAction<string | null>>;
  pendingMoveRosterId: string | null;
  pendingMoveDestination: string;
  setPendingMoveDestination: Dispatch<SetStateAction<string>>;
  movedAlert: MovedAlertState | null;
  setMovedAlert: Dispatch<SetStateAction<MovedAlertState | null>>;
  derivedRequiredHeadcount: number;
  rostersInFallback: number;
  movingRoster: RosterConfig | null;
  moveDestinationSites: Array<{ id: string; label: string }>;
  handleSiteChange: (field: SiteFieldKey, value: string) => void;
  discardSiteChanges: () => void;
  saveSiteChanges: () => void;
  handleRosterChange: (id: string, patch: Partial<RosterConfig>) => void;
  handleRevertField: (id: string, field: "lateArrival") => void;
  handleToggleGeofence: (id: string, useSite: boolean) => void;
  confirmRevertGeofence: () => void;
  saveRoster: (id: string) => void;
  discardRoster: (id: string) => void;
  openMoveDialog: (rosterId: string) => void;
  closeMoveDialog: () => void;
  confirmMove: () => void;
}

/**
 * Page-level state for the per-Site deep config page.
 *
 * The Site defaults + Rosters come from `GET /shift-sites/{siteId}/configuration/`.
 * The query data seeds editable local state once it loads; subsequent edits stay
 * local until saved. The page remounts via `key={siteId}`, so seeding happens
 * once per Site visit.
 *
 *  - `notFound` is true when the configuration request 404s.
 *  - `lastSavedRosters` tracks the most recent saved snapshot per Roster so
 *    Discard rolls back to the last save (not the original load).
 *  - Address has no backend column: it is a local-only, non-persisted value.
 */
export function useSiteConfiguration(
  siteId: string
): UseSiteConfigurationResult {
  const configQuery = useSiteConfigurationApiService(siteId);
  const treeQuery = useSitesAndRegionsTreeApiService();

  const configureSiteMutation = useConfigureSiteMutation();
  const configureRosterMutation = useConfigureRosterMutation();
  const moveRosterMutation = useMoveRosterMutation();

  const notFound =
    configQuery.isError &&
    (configQuery.error as AxiosError)?.response?.status === 404;

  const [site, setSite] = useState<SiteConfig>(EMPTY_SITE);
  const [savedSite, setSavedSite] = useState<SiteConfig>(EMPTY_SITE);
  const [rosters, setRosters] = useState<RosterConfig[]>([]);
  const [lastSavedRosters, setLastSavedRosters] = useState<
    Record<string, RosterConfig>
  >({});
  const [seeded, setSeeded] = useState(false);

  /* -------------- Seed editable state from the query data -------------- */
  useEffect(() => {
    if (seeded || !configQuery.data) return;
    const { site: loadedSite, rosters: loadedRosters } = configQuery.data;
    setSite(loadedSite);
    setSavedSite(loadedSite);
    setRosters(loadedRosters);
    setLastSavedRosters(
      Object.fromEntries(loadedRosters.map((r) => [r.id, r]))
    );
    setSeeded(true);
  }, [configQuery.data, seeded]);

  const [siteDirty, setSiteDirty] = useState(false);
  const [siteDirtyField, setSiteDirtyField] = useState<SiteFieldKey | null>(
    null
  );
  const [postSaveAlert, setPostSaveAlert] = useState<PostSaveAlertState | null>(
    null
  );
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [pendingRevertId, setPendingRevertId] = useState<string | null>(null);
  const [pendingMoveRosterId, setPendingMoveRosterId] = useState<string | null>(
    null
  );
  const [pendingMoveDestination, setPendingMoveDestination] =
    useState<string>("");
  const [movedAlert, setMovedAlert] = useState<MovedAlertState | null>(null);

  /* -------------- Derived values -------------- */
  const derivedRequiredHeadcount = useMemo(
    () =>
      rosters.reduce((acc, r) => {
        if (r.shiftTypes && r.shiftTypes.length > 0) {
          return (
            acc +
            r.shiftTypes.reduce((sAcc, s) => {
              const req = s.requiredHeadcount
                ? Number(s.requiredHeadcount)
                : NaN;
              return sAcc + (Number.isFinite(req) ? req : s.assignedHeadcount);
            }, 0)
          );
        }
        const req = r.requiredHeadcount ? Number(r.requiredHeadcount) : NaN;
        return acc + (Number.isFinite(req) ? req : r.assignedHeadcount);
      }, 0),
    [rosters]
  );

  const rostersInFallback = useMemo(
    () =>
      rosters.filter((r) => {
        if (r.shiftTypes && r.shiftTypes.length > 0) {
          return r.shiftTypes.some((s) => !s.requiredHeadcount);
        }
        return !r.requiredHeadcount;
      }).length,
    [rosters]
  );

  const movingRoster = useMemo(
    () => rosters.find((r) => r.id === pendingMoveRosterId) ?? null,
    [rosters, pendingMoveRosterId]
  );

  // Destination Sites for the Move Roster dialog: every Site across all Regions
  // except the current one.
  const moveDestinationSites = useMemo(
    () =>
      (treeQuery.data ?? []).flatMap((region) =>
        region.sites
          .filter((s) => s.id !== siteId)
          .map((s) => ({ id: s.id, label: `${s.name} (${region.name})` }))
      ),
    [treeQuery.data, siteId]
  );

  /* -------------- Site change semantics -------------- */
  function handleSiteChange(field: SiteFieldKey, value: string): void {
    setSite((s) => ({ ...s, [field]: value }));
    if (value !== savedSite[field as keyof typeof savedSite]) {
      setSiteDirty(true);
      setSiteDirtyField(field);
    }
  }

  function discardSiteChanges(): void {
    setSite(savedSite);
    setSiteDirty(false);
    setSiteDirtyField(null);
  }

  function saveSiteChanges(): void {
    const field = siteDirtyField;
    if (!field) return;

    // Address has no backend column — saving it is a local-only no-op.
    if (field === "address") {
      setSavedSite(site);
      setSiteDirty(false);
      setSiteDirtyField(null);
      return;
    }

    const payload: ConfigureSiteRequest = {};
    if (field === "siteName") payload.siteName = site.siteName;
    if (field === "latitude") payload.latitude = site.latitude;
    if (field === "longitude") payload.longitude = site.longitude;
    if (field === "geofenceRadius")
      payload.geofenceRadius = site.geofenceRadius;
    if (field === "lateArrivalThreshold") {
      payload.lateArrivalThreshold = site.lateArrivalThreshold;
    }

    let oldValue = "";
    let newValue = "";
    if (field === "lateArrivalThreshold") {
      oldValue = `${savedSite.lateArrivalThreshold} min`;
      newValue = `${site.lateArrivalThreshold} min`;
    } else {
      oldValue = String(savedSite[field as keyof typeof savedSite]);
      newValue = String(site[field as keyof typeof site]);
    }

    configureSiteMutation.mutate(
      { siteId, data: payload },
      {
        onSuccess: (response) => {
          setSavedSite(response.site);
          setSite(response.site);
          setSiteDirty(false);
          setSiteDirtyField(null);
          setPostSaveAlert({
            field: cascadeFieldLabel[field],
            oldValue,
            newValue,
            updated: response.cascade.updatedRosters,
            skipped: response.cascade.skippedRosters,
          });
        },
      }
    );
  }

  /* -------------- Roster change semantics -------------- */
  function handleRosterChange(id: string, patch: Partial<RosterConfig>): void {
    setRosters((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function handleRevertField(id: string, field: "lateArrival"): void {
    if (field === "lateArrival") {
      handleRosterChange(id, {
        lateArrivalSource: "site",
        lateArrivalOverrideValue: undefined,
        dirty: true,
      });
    }
  }

  function handleToggleGeofence(id: string, useSite: boolean): void {
    const roster = rosters.find((r) => r.id === id);
    if (!roster) return;

    // Off → On with override values present: confirm.
    if (
      useSite &&
      !roster.useSiteGeofence &&
      (roster.geofenceAddress ||
        roster.geofenceLatitude ||
        roster.geofenceLongitude ||
        roster.geofenceRadius)
    ) {
      setPendingRevertId(id);
      return;
    }

    // Off → On with no values, or On → Off.
    if (useSite) {
      handleRosterChange(id, {
        useSiteGeofence: true,
        geofenceAddress: undefined,
        geofenceLatitude: undefined,
        geofenceLongitude: undefined,
        geofenceRadius: undefined,
        dirty: true,
      });
    } else {
      // Switching to override — seed with the current Site values.
      handleRosterChange(id, {
        useSiteGeofence: false,
        geofenceAddress: site.address,
        geofenceLatitude: site.latitude,
        geofenceLongitude: site.longitude,
        geofenceRadius: site.geofenceRadius,
        dirty: true,
      });
    }
  }

  function confirmRevertGeofence(): void {
    if (!pendingRevertId) return;
    handleRosterChange(pendingRevertId, {
      useSiteGeofence: true,
      geofenceAddress: undefined,
      geofenceLatitude: undefined,
      geofenceLongitude: undefined,
      geofenceRadius: undefined,
      dirty: true,
    });
    setPendingRevertId(null);
  }

  function saveRoster(id: string): void {
    const roster = rosters.find((r) => r.id === id);
    if (!roster) return;

    // Send strings (the string-typed contract). An empty string clears the
    // value server-side; the backend coerces "" -> NULL (inherit / fallback).
    const payload: ConfigureRosterRequest = {
      lateArrivalSource: roster.lateArrivalSource,
      lateArrivalOverrideValue:
        roster.lateArrivalSource === "override"
          ? roster.lateArrivalOverrideValue ?? ""
          : "",
      useSiteGeofence: roster.useSiteGeofence,
    };

    if (roster.shiftTypes && roster.shiftTypes.length > 0) {
      payload.shiftTypes = roster.shiftTypes.map((s) => ({
        id: s.id,
        requiredHeadcount: s.requiredHeadcount ?? "",
      }));
    } else {
      payload.requiredHeadcount = roster.requiredHeadcount ?? "";
    }

    if (!roster.useSiteGeofence) {
      payload.geofenceLatitude = roster.geofenceLatitude ?? "";
      payload.geofenceLongitude = roster.geofenceLongitude ?? "";
      payload.geofenceRadius = roster.geofenceRadius ?? "";
    }

    configureRosterMutation.mutate(
      { rosterId: id, siteId, data: payload },
      {
        onSuccess: (updatedRoster) => {
          setRosters((rs) => rs.map((r) => (r.id === id ? updatedRoster : r)));
          setLastSavedRosters((prev) => ({ ...prev, [id]: updatedRoster }));
        },
      }
    );
  }

  function discardRoster(id: string): void {
    // Restore the most recent saved snapshot.
    const restored = lastSavedRosters[id];
    if (restored) {
      setRosters((rs) => rs.map((r) => (r.id === id ? restored : r)));
    }
  }

  /* -------------- Roster movement between Sites -------------- */
  function openMoveDialog(rosterId: string): void {
    setPendingMoveRosterId(rosterId);
    setPendingMoveDestination("");
  }

  function closeMoveDialog(): void {
    setPendingMoveRosterId(null);
    setPendingMoveDestination("");
  }

  function confirmMove(): void {
    if (!pendingMoveRosterId || !pendingMoveDestination) return;
    const moved = rosters.find((r) => r.id === pendingMoveRosterId);
    if (!moved) return;
    const destinationLabel =
      moveDestinationSites.find((s) => s.id === pendingMoveDestination)
        ?.label ?? pendingMoveDestination;
    const movedId = pendingMoveRosterId;
    moveRosterMutation.mutate(
      {
        rosterId: movedId,
        siteId,
        data: { destination_site_id: pendingMoveDestination },
      },
      {
        onSuccess: () => {
          setRosters((rs) => rs.filter((r) => r.id !== movedId));
          setMovedAlert({
            rosterName: moved.name,
            destination: destinationLabel,
          });
          closeMoveDialog();
        },
      }
    );
  }

  return {
    notFound,
    isLoading: configQuery.isLoading,
    isError: configQuery.isError && !notFound,
    site,
    savedSite,
    rosters,
    lastSavedRosters,
    siteDirty,
    siteDirtyField,
    postSaveAlert,
    setPostSaveAlert,
    auditDrawerOpen,
    setAuditDrawerOpen,
    pendingRevertId,
    setPendingRevertId,
    pendingMoveRosterId,
    pendingMoveDestination,
    setPendingMoveDestination,
    movedAlert,
    setMovedAlert,
    derivedRequiredHeadcount,
    rostersInFallback,
    movingRoster,
    moveDestinationSites,
    handleSiteChange,
    discardSiteChanges,
    saveSiteChanges,
    handleRosterChange,
    handleRevertField,
    handleToggleGeofence,
    confirmRevertGeofence,
    saveRoster,
    discardRoster,
    openMoveDialog,
    closeMoveDialog,
    confirmMove,
  };
}

export default useSiteConfiguration;
